import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { groupsAPI, billsAPI } from '../services/api';
import { Group, GroupMember, Bill } from '../types';
import { AppLayout } from '../components/layout/AppLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { QuickAddMember, NewMember } from '../components/features/QuickAddMember';
import { BillUploader } from '../components/features/BillUploader';

interface SimplifiedDebt {
  from: { userId: string; name: string };
  to: { userId: string; name: string };
  amount: number;
}

const GroupDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [simplifiedDebts, setSimplifiedDebts] = useState<SimplifiedDebt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showUploadBill, setShowUploadBill] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingMember, setEditingMember] = useState<GroupMember | null>(null);
  const [editForm, setEditForm] = useState({ isVegetarian: false, drinksAlcohol: false, name: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadGroupData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadGroupData = async () => {
    try {
      const [groupData, billsData, balancesData] = await Promise.all([
        groupsAPI.getById(id!),
        billsAPI.getAll(id),
        groupsAPI.getGroupBalances(id!).catch(() => ({ simplifiedDebts: [] })),
      ]);
      setGroup(groupData.group);
      setMembers(groupData.members);
      setBills(billsData.bills);
      setSimplifiedDebts(balancesData.simplifiedDebts || []);
    } catch (error) {
      console.error('Failed to load group:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (member: NewMember) => {
    try {
      await groupsAPI.addMember(id!, {
        guestName: member.name,
        isVegetarian: member.isVegetarian,
        drinksAlcohol: member.drinksAlcohol,
      });
      loadGroupData();
    } catch (error) {
      console.error('Failed to add member:', error);
      alert('Failed to add member');
    }
  };

  const handleEditMember = (member: GroupMember) => {
    setEditingMember(member);
    setEditForm({
      isVegetarian: member.is_vegetarian,
      drinksAlcohol: member.drinks_alcohol,
      name: member.name,
    });
  };

  const handleSaveMemberPreferences = async () => {
    if (!editingMember) return;
    setSaving(true);
    try {
      await groupsAPI.updateMemberPreferences(id!, editingMember.id, {
        isVegetarian: editForm.isVegetarian,
        drinksAlcohol: editForm.drinksAlcohol,
        guestName: editingMember.is_guest ? editForm.name : undefined,
      });
      setEditingMember(null);
      loadGroupData();
    } catch (error) {
      console.error('Failed to update member:', error);
      alert('Failed to update member preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadBill = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('bill', file);
      formData.append('groupId', id!);

      const result = await billsAPI.upload(formData);
      setShowUploadBill(false);
      navigate(`/bills/${result.bill.id}`);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload bill');
    } finally {
      setUploading(false);
    }
  };

  const getGroupEmoji = (name: string): string => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('trip') || lowerName.includes('vacation')) return '✈️';
    if (lowerName.includes('dinner') || lowerName.includes('lunch') || lowerName.includes('food')) return '🍽️';
    if (lowerName.includes('party')) return '🎉';
    if (lowerName.includes('office') || lowerName.includes('work')) return '💼';
    if (lowerName.includes('home') || lowerName.includes('flat') || lowerName.includes('roommate')) return '🏠';
    return '👥';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout showNav={false}>
      <Header
        showBack
        rightAction={
          <button
            onClick={() => {/* TODO: Show options menu */}}
            className="p-2 rounded-xl hover:bg-slate-100"
          >
            <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        }
      />

      {/* Group Header */}
      <div className="px-4 pb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-3xl">
            {getGroupEmoji(group?.name || '')}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{group?.name}</h1>
            {group?.description && (
              <p className="text-slate-500">{group.description}</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => setShowUploadBill(true)}
            fullWidth
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              </svg>
            }
          >
            Add Bill
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowAddMember(true)}
            fullWidth
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            }
          >
            Add Person
          </Button>
        </div>
      </div>

      {/* Tabs-like sections */}
      <div className="px-4 space-y-6 pb-8">
        {/* Members Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">
              Members ({members.length})
            </h2>
          </div>

          <div className="space-y-2">
            {members.map((member) => (
              <Card key={member.id} padding="sm">
                <div className="flex items-center gap-3">
                  <Avatar name={member.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 truncate">{member.name}</p>
                      {member.role === 'admin' && (
                        <Badge variant="default" size="sm">Admin</Badge>
                      )}
                      {member.is_guest && (
                        <Badge variant="warning" size="sm">Guest</Badge>
                      )}
                    </div>
                    <div className="flex gap-1.5 mt-0.5">
                      {member.is_vegetarian ? (
                        <Badge variant="veg" size="sm">Veg</Badge>
                      ) : (
                        <Badge variant="nonveg" size="sm">Non-veg</Badge>
                      )}
                      {member.drinks_alcohol && (
                        <Badge variant="alcohol" size="sm">Drinks</Badge>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleEditMember(member)}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-400"
                    title="Edit preferences"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Balances Section */}
        {simplifiedDebts.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">
                Settle Up
              </h2>
            </div>

            <Card className="bg-amber-50 border-amber-100">
              <div className="space-y-3">
                {simplifiedDebts.map((debt, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Avatar name={debt.from.name} size="sm" />
                    <div className="flex-1 flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900">{debt.from.name}</span>
                      <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                      <span className="font-medium text-slate-900">{debt.to.name}</span>
                    </div>
                    <span className="font-bold text-amber-700">
                      ₹{debt.amount.toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-amber-600 mt-3 pt-3 border-t border-amber-200">
                Simplified payments to settle all debts
              </p>
            </Card>
          </section>
        )}

        {/* Bills Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">
              Bills ({bills.length})
            </h2>
          </div>

          {bills.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No bills yet</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Upload your first bill to start splitting
                </p>
                <Button onClick={() => setShowUploadBill(true)} size="sm">
                  Add Bill
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-2">
              {bills.map((bill) => (
                <Card
                  key={bill.id}
                  hover
                  onClick={() => navigate(`/bills/${bill.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {bill.restaurant_name || 'Unknown Restaurant'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(bill.created_at).toLocaleDateString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        ₹{Number(bill.total).toLocaleString('en-IN')}
                      </p>
                      <Badge
                        variant={
                          bill.status === 'settled' ? 'success' :
                          bill.status === 'finalized' ? 'default' : 'warning'
                        }
                        size="sm"
                      >
                        {bill.status}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Add Member Modal */}
      <QuickAddMember
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
      />

      {/* Upload Bill Modal */}
      <Modal
        isOpen={showUploadBill}
        onClose={() => setShowUploadBill(false)}
        title="Add Bill"
        size="lg"
      >
        <BillUploader onUpload={handleUploadBill} uploading={uploading} />
      </Modal>

      {/* Edit Member Modal */}
      <Modal
        isOpen={!!editingMember}
        onClose={() => setEditingMember(null)}
        title="Edit Member"
      >
        {editingMember && (
          <div className="space-y-4">
            {/* Name (only editable for guests) */}
            {editingMember.is_guest && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  placeholder="Guest name"
                />
              </div>
            )}

            {/* Dietary Preference */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Dietary Preference
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, isVegetarian: true })}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    editForm.isVegetarian
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-xl mb-1 block">🥬</span>
                  <span className={`text-sm font-medium ${editForm.isVegetarian ? 'text-green-700' : 'text-slate-600'}`}>
                    Vegetarian
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, isVegetarian: false })}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    !editForm.isVegetarian
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-xl mb-1 block">🍗</span>
                  <span className={`text-sm font-medium ${!editForm.isVegetarian ? 'text-orange-700' : 'text-slate-600'}`}>
                    Non-vegetarian
                  </span>
                </button>
              </div>
            </div>

            {/* Drinks Alcohol */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Drinks Alcohol?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, drinksAlcohol: true })}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    editForm.drinksAlcohol
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-xl mb-1 block">🍺</span>
                  <span className={`text-sm font-medium ${editForm.drinksAlcohol ? 'text-purple-700' : 'text-slate-600'}`}>
                    Yes
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, drinksAlcohol: false })}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    !editForm.drinksAlcohol
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-xl mb-1 block">🚫</span>
                  <span className={`text-sm font-medium ${!editForm.drinksAlcohol ? 'text-blue-700' : 'text-slate-600'}`}>
                    No
                  </span>
                </button>
              </div>
            </div>

            <Button
              onClick={handleSaveMemberPreferences}
              loading={saving}
              fullWidth
            >
              Save Changes
            </Button>
          </div>
        )}
      </Modal>
    </AppLayout>
  );
};

export default GroupDetails;
